import { type DefaultValueConverters } from '@databases/collab/default-value-converters';
import { YDatabase } from '@databases/collab/y-database';
import { CollabManager } from '@databases/collab-state-manager/collab-manager';
import { UserType } from '@databases/model/value';
import { DatabaseSdk } from '@databases/sdk';

import { type FrameToken } from '@canvas/frame-api-types';
import { Action } from '@canvas/model/permissions';

import { awaitLoaded } from './await-loaded.ts';
import {
  type CollabManagerFactory,
  type CollabManagerLike
} from './collab-manager-like.ts';
import { ConnectedDatabase } from './connected-database.ts';

const DEFAULT_TIMEOUT_MS = 30_000;

export const noopGetCurrentUser: DefaultValueConverters['getCurrentUser'] = () => ({
  id: '',
  type: UserType.Referenced
});

type BaseLoadDatabaseOptions = {
  /** Atlassian cloud / tenant ID. */
  cloudId: string;
  /** Confluence activation ID (used for analytics and relay routing). */
  activationId: string;
  /** Confluence space ID that contains this database. */
  spaceId: string;
  /** Confluence content (page) ID for this database. */
  contentId: string;
  /** Canvas document reference ID for the underlying Yjs document. */
  referenceId: string;
  /**
   * Returns the current user identity for default-value resolution
   * (e.g. "Created by" fields). Falls back to an anonymous stub.
   */
  getCurrentUser?: DefaultValueConverters['getCurrentUser'];
  /**
   * Maximum time in milliseconds to wait for the initial document load.
   * Defaults to 30 000 ms.
   */
  timeoutMs?: number;
  /**
   * When `true`, forces the connection into read-only mode regardless
   * of the token's grants. The underlying WebSocket will not send
   * document updates. Useful for scripts that must never mutate.
   */
  readOnly?: boolean;
};

export type LoadDatabaseOptions = BaseLoadDatabaseOptions &
  (
    | { token: FrameToken }
    | { token: FrameToken; onRefreshToken: () => Promise<FrameToken> }
  );

export type LoadDatabaseDeps = {
  createCollabManager: CollabManagerFactory;
};

const defaultDeps: LoadDatabaseDeps = {
  createCollabManager: (
    cloudId,
    activationId,
    spaceId,
    contentId,
    referenceId,
    ydb,
    token
  ) =>
    new CollabManager(cloudId, activationId, spaceId, contentId, referenceId, ydb, token)
};

function enforceReadOnly(manager: CollabManagerLike): void {
  const originalUpdate = manager.onPermissionsUpdate.bind(manager);

  manager.tokenManager.onSynchronizeClaims((id, claims) => {
    const readOnlyGrants = new Set(claims.board);
    readOnlyGrants.delete(Action.write);
    originalUpdate(id, { board: readOnlyGrants });
  });
}

/**
 * Creates a `loadDatabase` function with injected dependencies.
 *
 * Production code uses the default export {@link loadDatabase}.
 * Tests can call `createLoader({ createCollabManager: ... })` to inject
 * mocks without `jest.mock()`.
 */
export function createLoader(deps: LoadDatabaseDeps = defaultDeps) {
  /**
   * Loads a Confluence Database over a live Yjs collab connection and returns
   * a {@link ConnectedDatabase} wrapping a {@link DatabaseSdk}.
   *
   * This bypasses Redux entirely — the database is loaded directly via
   * `CollabManager` and the Yjs WebSocket protocol. The returned
   * `ConnectedDatabase` must be {@link ConnectedDatabase.dispose | disposed}
   * when no longer needed to tear down the WebSocket.
   *
   * @example
   * ```ts
   * import { loadDatabase } from '@databases/sdk-loader';
   *
   * const db = await loadDatabase({
   *   cloudId: 'tenant-abc',
   *   activationId: 'act-123',
   *   spaceId: '456',
   *   contentId: 'page-789',
   *   referenceId: 'ref-xyz',
   *   token: myFrameToken,
   * });
   *
   * const result = db.sdk.fields.add({ name: 'Status', type: 'single-select' });
   *
   * db.dispose();
   * ```
   *
   * @throws {LoadTimeoutError} If the initial document is not received within
   *   `opts.timeoutMs` (default 30 s).
   */
  return async function loadDatabase(
    opts: LoadDatabaseOptions
  ): Promise<ConnectedDatabase> {
    const converters: DefaultValueConverters = {
      getCurrentUser: opts.getCurrentUser ?? noopGetCurrentUser
    };
    const ydb = new YDatabase(undefined, converters);

    const manager = deps.createCollabManager(
      opts.cloudId,
      opts.activationId,
      opts.spaceId,
      opts.contentId,
      opts.referenceId,
      ydb,
      opts.token
    );

    if ('onRefreshToken' in opts) {
      manager.tokenManager.onRefreshToken(opts.onRefreshToken);
    }

    if (opts.readOnly) {
      enforceReadOnly(manager);
    }

    manager.connect();

    try {
      await awaitLoaded(manager, opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
    } catch (error) {
      manager.killSession();
      throw error;
    }

    const sdk = new DatabaseSdk(ydb);
    const isReadOnly = opts.readOnly ?? manager.isReadOnly;

    return new ConnectedDatabase(sdk, manager, ydb, isReadOnly);
  };
}

/**
 * Loads a Confluence Database over a live Yjs collab connection.
 *
 * @see {@link createLoader} for dependency injection in tests.
 */
export const loadDatabase = createLoader();
